import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseFectch';

export interface ComplianceProfile {
  _id?: string;
  companyId: string;
  dataResidencyRegion: string;
  complianceProfile?: {
    frameworks?: string[];
    dpoName?: string;
    dpoEmail?: string;
    breachNotificationHours?: number;
    retentionYears?: number;
  };
}
export interface ConsentRecord {
  _id?: string;
  companyId: string;
  userId: string;
  consentType: string;
  version: string;
  given: boolean;
  method?: string;
  ip?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
  withdrawnAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DeletionRequest {
  _id?: string;
  userId: string;
  requestType: 'data_export' | 'data_delete' | 'account_deletion';
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  createdAt?: string;
  retentionDeadline?: string;
}

export interface AuditLog {
  _id?: string;
  companyId?: string;
  userId?: string;
  action: string;
  resource?: string;
  method?: string;
  path?: string;
  ip?: string;
  userAgent?: string;
  metadata?: { statusCode?: number; durationMs?: number };
  createdAt?: string;
}

export interface ExportResult {
  data: Record<string, any>;
  exportedAt: string;
}

const complianceApi = createApi({
  reducerPath: 'complianceApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'ComplianceProfile',
    'DeletionRequests',
    'AuditLogs',
    'ConsentRecords',
  ],
  endpoints: (builder) => ({
    getComplianceProfile: builder.query<ComplianceProfile, void>({
      query: () => ({ url: '/compliance/profile', method: 'GET' }),
      providesTags: ['ComplianceProfile'],
    }),

    updateComplianceProfile: builder.mutation<
      ComplianceProfile,
      Partial<ComplianceProfile>
    >({
      query: (body) => ({
        url: '/compliance/profile',
        method: 'PUT', // matches router.put, not PATCH
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['ComplianceProfile'],
    }),

    exportMyData: builder.mutation<ExportResult, void>({
      query: () => ({
        url: '/compliance/export-data',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    createDeletionRequest: builder.mutation<any, DeletionRequest>({
      query: (body) => ({
        url: '/compliance/deletion-request',
        method: 'POST',
        body, // field name now matches controller's req.body.requestType
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['DeletionRequests'],
    }),

    getDeletionRequests: builder.query<
      DeletionRequest[],
      { userId?: string } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.userId) params.append('userId', args.userId);
        return {
          url: `/compliance/deletion-requests?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['DeletionRequests'],
    }),
    recordConsent: builder.mutation<
      void,
      {
        consentType: string;
        version: string;
        given: boolean;
        method?: string;
        metadata?: Record<string, unknown>;
      }
    >({
      query: (body) => ({
        url: '/compliance/consent',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    getConsentRecords: builder.query<ConsentRecord[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/compliance/consent/${userId}`,
        method: 'GET',
      }),
      providesTags: ['ConsentRecords'],
    }),
    getAuditLogs: builder.query<
      AuditLog[],
      { page?: number; limit?: number; userId?: string }
    >({
      query: ({ page = 1, limit = 50, userId }) => {
        const offset = (page - 1) * limit; // backend expects offset, not page
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        if (userId) params.append('userId', userId);
        return {
          url: `/compliance/audit-logs?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['AuditLogs'],
    }),
  }),
});

export const {
  useGetComplianceProfileQuery,
  useUpdateComplianceProfileMutation,
  useExportMyDataMutation,
  useCreateDeletionRequestMutation,
  useGetDeletionRequestsQuery,
  useGetAuditLogsQuery,
  useRecordConsentMutation,
  useLazyGetConsentRecordsQuery,
} = complianceApi;

export default complianceApi;
